import React, { ReactNode, useMemo, useState } from "react";
import { Button, Col, Collapse, DatePicker, Form, Input, Row, Select, Spin, Tooltip } from "antd";
import moment, { Moment } from "moment";
import { FormInstance } from "antd/es/form/Form";
import { useParams } from "react-router";
import { useAppDispatch, useAppSelector } from "../../../..";
import {
  checkIsLoading,
  displayFormat,
  getDictionaryItemById,
  getSelectOptions,
  limitText,
  toDisplayFormat,
} from "../../../../lib/utils";
import { IChangingRecordKeyNumber } from "../../../../lib/types/IViewMode";
import { ReactComponent as EditIcon } from "../../../../img/versions_icons/editVersion_icon.svg";
import { ReactComponent as CloseDateIcon } from "../../../../img/versions_icons/closeVersion_icon.svg";
import { ReactComponent as SuccessIcon } from "../../../../img/buttons_icons/submitButton_icon.svg";
import { hasAccess } from "../../../../components/RolesModule/hasAccess";
import { MENU_ADMIN_OBJECT_CHAR_TYPE_WRITE } from "../../../../lib/const/permissions";
import "./TypesCharacteristicsObjectParams.less";
import { TypeCharacteristic } from "../../types/TypeCharacteristic";
import { searchTypesCharacteristics } from "../../store/slice/typesCharacteristicsSlice";
import { TypeCharacteristicObject } from "../../types/TypeCharacteristicObject";
import { ReactComponent as DotsIcon } from "../../../../img/3dots.svg";
import { ReactComponent as CancelIcon } from "../../../../img/cancel.svg";
import HistoryAndPropertyModal from "../../../../components/HistoryAndPropertyModal/HistoryAndPropertyModal";
import { getCamelizeDictionaries } from "../TypesCharacteristicsObjectValues/utils/getCamelizeDictionaries";

interface ComponentProps {
  mode: "create" | "view" | "edit_close_date" | "edit";
  childChangingMode?: IChangingRecordKeyNumber;
  typeCharacteristicObject?: TypeCharacteristicObject;
  typesCharacteristicsObjectParamsForm?: FormInstance<any>;
  isLoading?: boolean;
  isChild?: boolean;
  isLatestVersion?: boolean;
  isEditingDisabled?: boolean;
  onSubmit?: () => void;
  onCancel?: () => void;
  changeViewMode?: (params?: IChangingRecordKeyNumber) => void;
  idx?: number;
  setActiveTypesCharacteristicsVersion?: (
    value: React.SetStateAction<TypeCharacteristic | undefined>
  ) => void;
  selectedCharType?: TypeCharacteristic;
  onSelectedCharType: (charTypeId: number) => void;
  setMode?: (value: React.SetStateAction<"view" | "edit_close_date" | "edit">) => void;
  targetKeys: string[];
}

export const TypesCharacteristicsObjectParams = ({
  mode,
  childChangingMode,
  typeCharacteristicObject,
  typesCharacteristicsObjectParamsForm,
  isLoading = false,
  isChild = false,
  isLatestVersion,
  isEditingDisabled,
  onSubmit,
  onCancel,
  changeViewMode,
  idx,
  setActiveTypesCharacteristicsVersion,
  selectedCharType,
  onSelectedCharType,
  setMode,
  targetKeys,
}: ComponentProps) => {
  const {
    typesCharacteristicsById,
    typesCharacteristics,
    typesCharacteristicsLoading,
    typesCharacteristicsObjectObjectTypesDic,
    typesCharacteristicsObjectObjectTypesDicLoading,
    typesCharacteristicsTypesStorageDic,
    typesCharacteristicsTypesStorageDicLoading,
    typesCharacteristicsObjectAccessModesDic,
    typesCharacteristicsObjectAccessModesDicLoading,
    typesCharacteristicsViewModeMaskDic,
    typesCharacteristicsViewModeMaskDicLoading,
  } = useAppSelector((state) => state.typesCharacteristicsObject);

  const { charTypeId } = useParams<{ charTypeId: string }>();

  const [historyModalVisible, setHistoryModalVisible] = useState(false);
  const [objectId, setObjectId] = useState<number | null>(null);

  const showHistoryModal = (objectId: number) => {
    setHistoryModalVisible(true);
    setObjectId(objectId);
  };

  const dispatch = useAppDispatch();

  interface Props {
    label: string;
    data?: ReactNode;
    offsetCol?: number;
    spanCol?: number;
    labelCol?: number;
    wrapperCol?: number;
    tooltipTitle?: string;
  }

  const renderDataItem = (props: Props): ReactNode => {
    const {
      label,
      data,
      offsetCol = 0,
      spanCol = 6,
      labelCol = 8,
      wrapperCol = 16,
      tooltipTitle,
    } = props;
    return (
      <Col offset={offsetCol} span={spanCol}>
        <Row className="types-characteristics-params__dataRow">
          <Col span={labelCol}>
            {tooltipTitle ? (
              <Tooltip title={tooltipTitle}>
                <label>{label}</label>
              </Tooltip>
            ) : (
              <label>{label}</label>
            )}
          </Col>
          <Col span={wrapperCol}>
            <span>{data}</span>
          </Col>
        </Row>
      </Col>
    );
  };

  const childClassName = useMemo(() => {
    if (!isChild) return "";
    return "row_child";
  }, [typeCharacteristicObject, isChild]);

  const [valueCharType, setValueCharType] = useState<string>("");

  const handleSearchCharType = async (value: string) => {
    setValueCharType(value);

    if (value.length < 3) return; // Не искать, если менее 3 символов

    dispatch(searchTypesCharacteristics({ name: value }));
  };

  const [activeCollapseKey, setActiveCollapseKey] = useState<string | undefined>("1");

  const onChangeCollapse = (key: string | string[]) => {
    if (typeof key === "string") {
      setActiveCollapseKey(key);
    } else {
      setActiveCollapseKey(key[0]);
    }
  };

  const onChangeFromDate = (date: Moment | null) => {
    if (typesCharacteristicsObjectParamsForm) {
      typesCharacteristicsObjectParamsForm.setFieldsValue({ fromDateValues: date });
    }
  };

  const renderParamsPanel = () => {
    const getIsSelectedForValuesVisible = () => {
      let isVisible = false;

      if (selectedCharType?.charTypeValues && selectedCharType?.charTypeValues.length > 0) {
        isVisible = true;
      }

      return isVisible;
    };

    const isSelectedForValuesVisible = getIsSelectedForValuesVisible();

    const getRenderDefaultValueViewMode = () => {
      // Функция для отображения значения по умолчанию, когда оно берётся не из значений
      const getFormatDefaultValue = (typeCharacteristicObject?: TypeCharacteristicObject) => {
        const defVal = typeCharacteristicObject?.defaultValue;

        switch (selectedCharType?.dataTypeId) {
          case 3:
            return defVal ? toDisplayFormat(defVal) : "";
          case 4:
            return defVal
              ? getDictionaryItemById(
                  getCamelizeDictionaries(selectedCharType.valueDicTypeAlias),
                  Number(defVal)
                )?.name
              : "";

          default:
            return defVal;
        }
      };

      // Функция для отображения значения по умолчанию, когда оно берётся из значений
      const getFormatDefaultValueForValues = (el?: TypeCharacteristicObject) => {
        const findSelectedValue = el?.objectCharTypeValues?.find((v) => {
          switch (selectedCharType?.dataTypeId) {
            case 3:
              return moment(v?.charTypeValue?.value).isSame(moment(el?.defaultValue), "day");
            default:
              return v?.charTypeValue?.value === el?.defaultValue;
          }
        });

        const val = findSelectedValue?.charTypeValue?.value;

        switch (selectedCharType?.dataTypeId) {
          case 3:
            return `${val ? toDisplayFormat(val) : ""}`;

          default:
            return `${val || ""}`;
        }
      };

      return renderDataItem({
        label: "Значение по умолчанию",
        data: isSelectedForValuesVisible
          ? getFormatDefaultValueForValues(typeCharacteristicObject)
          : getFormatDefaultValue(typeCharacteristicObject),
        labelCol: 11,
        wrapperCol: 13,
      });
    };

    return (
      <Row
        className={`types-characteristics-params ${childClassName}`}
        style={{ width: "100%", marginBottom: mode !== "create" ? 28 : undefined }}
      >
        <Spin spinning={isLoading}>
          <Row
            gutter={40}
            style={{ width: "100%", marginBottom: mode !== "create" ? 18 : undefined }}
          >
            {mode === "create" ? (
              <Col span={6}>
                <Form.Item
                  label={<div style={{ whiteSpace: "normal" }}>Тип характеристики</div>}
                  name="charTypeId"
                  labelCol={{ span: 10 }}
                  wrapperCol={{ span: 14 }}
                  rules={[
                    {
                      required: true,
                      message: "",
                    },
                  ]}
                >
                  <Select
                    options={getSelectOptions({
                      list:
                        valueCharType.length > 2 || selectedCharType ? typesCharacteristics : [],
                    })}
                    loading={checkIsLoading(typesCharacteristicsLoading)}
                    style={{ width: "100%" }}
                    placeholder="Введите текст..."
                    showSearch
                    allowClear
                    disabled={charTypeId !== undefined}
                    onSearch={handleSearchCharType} // Вызов при вводе текста
                    filterOption={false} // Отключаем стандартную фильтрацию
                    onChange={(e) => {
                      onSelectedCharType(Number(e));
                    }}
                  />
                </Form.Item>
              </Col>
            ) : (
              <>
                {renderDataItem({
                  label: "Тип характеристики",
                  data:
                    typeCharacteristicObject?.charTypeId && typesCharacteristicsById?.name
                      ? typesCharacteristicsById?.name
                      : "",
                  labelCol: 10,
                  wrapperCol: 14,
                })}
              </>
            )}

            {mode === "create" || mode === "edit" ? (
              <Col span={6}>
                <Form.Item
                  label="Наименование"
                  name="name"
                  labelCol={{ span: 9 }}
                  wrapperCol={{ span: 15 }}
                  rules={[
                    {
                      required: true,
                      message: "",
                    },
                  ]}
                >
                  <Input maxLength={255} placeholder="Введите текст..." allowClear={false} />
                </Form.Item>
              </Col>
            ) : (
              <>
                {renderDataItem({
                  label: "Наименование",
                  data: typeCharacteristicObject?.name,
                  labelCol: 9,
                  wrapperCol: 15,
                })}
              </>
            )}

            {mode === "create" || mode === "edit" ? (
              <Col span={6}>
                <Form.Item
                  label={
                    <Tooltip title="мин | макс">
                      <div style={{ whiteSpace: "normal" }}>Количество значений</div>
                    </Tooltip>
                  }
                  labelCol={{ span: 11 }}
                  wrapperCol={{ span: 13 }}
                  dependencies={["minCardinality", "maxCardinality"]}
                >
                  {({ getFieldsValue, validateFields }) => {
                    const { minCardinality, maxCardinality } = getFieldsValue();

                    return (
                      <Row wrap={false}>
                        <Form.Item
                          label=""
                          name="minCardinality"
                          rules={[
                            {
                              validator: (_, value) => {
                                if (value && isNaN(value)) {
                                  return Promise.reject();
                                }
                                return Promise.resolve();
                              },
                            },
                            {
                              validator: (_, value) => {
                                let isSucces = true;

                                if (value && maxCardinality && isSucces) {
                                  isSucces = Number(value) <= Number(maxCardinality);
                                }
                                return isSucces ? Promise.resolve() : Promise.reject();
                              },
                            },
                          ]}
                          noStyle
                        >
                          <Input
                            style={{ width: 56, height: 32 }}
                            onChange={() => {
                              validateFields(["maxCardinality"]);
                            }}
                          />
                        </Form.Item>

                        <span className="cardinality-value">|</span>

                        <Form.Item
                          label=""
                          name="maxCardinality"
                          rules={[
                            {
                              validator: (_, value) => {
                                if (value && isNaN(value)) {
                                  return Promise.reject();
                                }
                                return Promise.resolve();
                              },
                            },
                            {
                              validator: (_, value) => {
                                let isSucces = true;

                                if (value && minCardinality && isSucces) {
                                  isSucces = Number(value) >= Number(minCardinality);
                                }
                                return isSucces ? Promise.resolve() : Promise.reject();
                              },
                            },
                          ]}
                          noStyle
                        >
                          <Input
                            style={{ width: 56, height: 32 }}
                            onChange={() => {
                              validateFields(["minCardinality"]);
                            }}
                          />
                        </Form.Item>
                      </Row>
                    );
                  }}
                </Form.Item>
              </Col>
            ) : (
              <>
                {renderDataItem({
                  label: "Количество значений",
                  data: (
                    <>
                      {typeCharacteristicObject?.minCardinality}
                      <span className="cardinality-value">|</span>
                      {typeCharacteristicObject?.maxCardinality}
                    </>
                  ),
                  labelCol: 11,
                  wrapperCol: 13,
                  tooltipTitle: "мин | макс",
                })}
              </>
            )}

            {mode === "create" ? (
              <Col span={6}>
                <Form.Item noStyle dependencies={["toDate"]}>
                  {({ getFieldsValue }) => {
                    const { toDate } = getFieldsValue();

                    return (
                      <Form.Item
                        label="Дата создания"
                        name="fromDate"
                        labelCol={{ span: 10 }}
                        wrapperCol={{ span: 14 }}
                        rules={[
                          {
                            required: true,
                            message: "",
                          },
                        ]}
                      >
                        <DatePicker
                          format={displayFormat}
                          style={{ width: 150 }}
                          allowClear={false}
                          onChange={onChangeFromDate}
                          disabledDate={(current) => {
                            if (mode === "create" && toDate) {
                              return current.isAfter(toDate, "day");
                            }
                            return false;
                          }}
                        />
                      </Form.Item>
                    );
                  }}
                </Form.Item>
              </Col>
            ) : (
              <>
                {renderDataItem({
                  label: "Дата создания",
                  data: typeCharacteristicObject?.fromDate
                    ? toDisplayFormat(typeCharacteristicObject?.fromDate)
                    : "",
                  labelCol: 10,
                  wrapperCol: 14,
                })}
              </>
            )}
          </Row>

          <Row
            gutter={40}
            style={{ width: "100%", marginBottom: mode !== "create" ? 18 : undefined }}
          >
            {mode === "create" ? (
              <Col span={6}>
                <Form.Item
                  label="Объект"
                  name="objectTypeId"
                  labelCol={{ span: 10 }}
                  wrapperCol={{ span: 14 }}
                  rules={[
                    {
                      required: true,
                      message: "",
                    },
                  ]}
                >
                  <Select
                    options={getSelectOptions({
                      list: [...typesCharacteristicsObjectObjectTypesDic].sort((a, b) =>
                        a?.name?.localeCompare(b?.name)
                      ),
                    })}
                    loading={checkIsLoading(typesCharacteristicsObjectObjectTypesDicLoading)}
                    style={{ width: "100%" }}
                    placeholder="Выбрать"
                    allowClear
                    showSearch
                    filterOption={(inputValue, option) => {
                      return String(option?.label)
                        ?.toLowerCase()
                        .includes(inputValue?.toLowerCase());
                    }}
                  />
                </Form.Item>
              </Col>
            ) : (
              <>
                {renderDataItem({
                  label: "Объект",
                  data: getDictionaryItemById(
                    typesCharacteristicsObjectObjectTypesDic,
                    typeCharacteristicObject?.objectTypeId
                  )?.name,
                  labelCol: 10,
                  wrapperCol: 14,
                })}
              </>
            )}

            {mode === "create" || mode === "edit" ? (
              <Col span={6}>
                <Form.Item
                  label="Alias"
                  name="alias"
                  labelCol={{ span: 9 }}
                  wrapperCol={{ span: 15 }}
                  rules={[
                    {
                      required: true,
                      message: "",
                    },
                  ]}
                >
                  <Input maxLength={255} placeholder="Введите текст..." allowClear={false} />
                </Form.Item>
              </Col>
            ) : (
              <>
                {renderDataItem({
                  label: "Alias",
                  data: typeCharacteristicObject?.alias,
                  labelCol: 9,
                  wrapperCol: 15,
                })}
              </>
            )}

            {mode === "create" ? (
              <Col span={6}>
                <Form.Item
                  label="Тип хранения"
                  name="storageTypeId"
                  labelCol={{ span: 11 }}
                  wrapperCol={{ span: 13 }}
                  rules={[
                    {
                      required: true,
                      message: "",
                    },
                  ]}
                >
                  <Select
                    options={getSelectOptions({
                      list: typesCharacteristicsTypesStorageDic,
                    })}
                    loading={checkIsLoading(typesCharacteristicsTypesStorageDicLoading)}
                    placeholder="Выбрать"
                    allowClear
                  />
                </Form.Item>
              </Col>
            ) : (
              <>
                {renderDataItem({
                  label: "Тип хранения",
                  data: getDictionaryItemById(
                    typesCharacteristicsTypesStorageDic,
                    typeCharacteristicObject?.storageTypeId
                  )?.name,
                  labelCol: 11,
                  wrapperCol: 13,
                })}
              </>
            )}

            {mode === "create" || mode === "edit_close_date" ? (
              <Col span={6}>
                <Form.Item noStyle dependencies={["fromDate"]}>
                  {({ getFieldsValue }) => {
                    const { fromDate } = getFieldsValue();

                    return (
                      <Form.Item
                        label="Дата окончания"
                        name="toDate"
                        labelCol={{ span: 10 }}
                        wrapperCol={{ span: 14 }}
                        className="form-item-required__hidden-error__mb0"
                        rules={[
                          {
                            required: mode === "edit_close_date",
                            message: "",
                          },
                        ]}
                      >
                        <DatePicker
                          format={displayFormat}
                          style={{ width: 150 }}
                          allowClear
                          disabledDate={(current) => {
                            if (mode === "edit_close_date") {
                              return (
                                current.isBefore(
                                  moment(typeCharacteristicObject?.fromDate),
                                  "day"
                                ) ||
                                current.isAfter(moment(typeCharacteristicObject?.toDate), "day")
                              );
                            }
                            if (mode === "create" && fromDate) {
                              return current.isBefore(fromDate, "day");
                            }
                            return false;
                          }}
                        />
                      </Form.Item>
                    );
                  }}
                </Form.Item>
              </Col>
            ) : (
              <>
                {renderDataItem({
                  label: "Дата окончания",
                  data: typeCharacteristicObject?.toDate
                    ? toDisplayFormat(typeCharacteristicObject?.toDate)
                    : "",
                  labelCol: 10,
                  wrapperCol: 14,
                })}
              </>
            )}
          </Row>

          <Row gutter={40} style={{ width: "100%" }}>
            {mode === "create" || mode === "edit" ? (
              <Col span={6}>
                <Form.Item
                  label="Доступ"
                  name="accessModeId"
                  labelCol={{ span: 10 }}
                  wrapperCol={{ span: 14 }}
                  rules={[
                    {
                      required: true,
                      message: "",
                    },
                  ]}
                >
                  <Select
                    options={getSelectOptions({
                      list: typesCharacteristicsObjectAccessModesDic,
                    })}
                    loading={checkIsLoading(typesCharacteristicsObjectAccessModesDicLoading)}
                    placeholder="Выбрать"
                    allowClear={false}
                  />
                </Form.Item>
              </Col>
            ) : (
              <>
                {renderDataItem({
                  label: "Доступ",
                  data: getDictionaryItemById(
                    typesCharacteristicsObjectAccessModesDic,
                    typeCharacteristicObject?.accessModeId
                  )?.name,
                  labelCol: 10,
                  wrapperCol: 14,
                })}
              </>
            )}

            {mode === "create" || mode === "edit" ? (
              <Col span={6}>
                <Form.Item
                  label={<div style={{ whiteSpace: "normal" }}>Тип отображения</div>}
                  name="viewModeMask"
                  labelCol={{ span: 9 }}
                  wrapperCol={{ span: 15 }}
                  rules={[
                    {
                      required: true,
                      message: "",
                    },
                  ]}
                >
                  <Select
                    options={getSelectOptions({
                      list: typesCharacteristicsViewModeMaskDic,
                    })}
                    loading={checkIsLoading(typesCharacteristicsViewModeMaskDicLoading)}
                    placeholder="Выбрать"
                    allowClear={false}
                    showArrow
                  />
                </Form.Item>
              </Col>
            ) : (
              <>
                {renderDataItem({
                  label: "Тип отображения",
                  data: (
                    <Tooltip
                      title={
                        getDictionaryItemById(
                          typesCharacteristicsViewModeMaskDic,
                          typeCharacteristicObject?.viewModeMask
                        )?.name
                      }
                    >
                      {limitText(
                        getDictionaryItemById(
                          typesCharacteristicsViewModeMaskDic,
                          typeCharacteristicObject?.viewModeMask
                        )?.name,
                        20
                      )}
                    </Tooltip>
                  ),
                  labelCol: 9,
                  wrapperCol: 15,
                })}
              </>
            )}

            {mode === "create" || mode === "edit" ? (
              <Col span={6}>
                <Form.Item noStyle dependencies={["charTypeId"]}>
                  {({ getFieldValue }) => {
                    const charTypeId = getFieldValue("charTypeId");

                    if (isSelectedForValuesVisible) {
                      if (mode === "create") {
                        const arrOptions = selectedCharType?.charTypeValues?.filter((el) => {
                          return targetKeys.includes(String(el.id));
                        });

                        const getValue = (el) => {
                          const val = el.value;

                          switch (selectedCharType?.dataTypeId) {
                            case 3:
                              return `${val ? toDisplayFormat(val) : ""}`;

                            default:
                              return `${val || ""}`;
                          }
                        };

                        return (
                          <Form.Item
                            label={
                              <div style={{ whiteSpace: "normal" }}>Значение по умолчанию</div>
                            }
                            className="form-item-required__hidden-error__mb0"
                            name="defaultValue"
                            labelCol={{ span: 11 }}
                            wrapperCol={{ span: 13 }}
                          >
                            <Select
                              style={{ width: "100%" }}
                              placeholder="Выберите значение"
                              allowClear
                              disabled={!charTypeId}
                            >
                              {arrOptions?.map((item) => (
                                <Select.Option key={item.id} value={item.id}>
                                  {getValue(item)}
                                </Select.Option>
                              ))}
                            </Select>
                          </Form.Item>
                        );
                      }

                      if (mode === "edit") {
                        const arrOptions = typeCharacteristicObject?.objectCharTypeValues;

                        const getValue = (el) => {
                          const val = el.charTypeValue?.value;
                          switch (selectedCharType?.dataTypeId) {
                            case 3:
                              return `${val ? toDisplayFormat(val) : ""}`;
                            default:
                              return `${val || ""}`;
                          }
                        };

                        return (
                          <Form.Item
                            label={
                              <div style={{ whiteSpace: "normal" }}>Значение по умолчанию</div>
                            }
                            className="form-item-required__hidden-error__mb0"
                            name="defaultValue"
                            labelCol={{ span: 11 }}
                            wrapperCol={{ span: 13 }}
                          >
                            <Select
                              style={{ width: "100%" }}
                              placeholder="Выберите значение"
                              allowClear
                              disabled={!charTypeId}
                            >
                              {arrOptions &&
                                arrOptions?.map((item) => (
                                  <Select.Option
                                    key={item?.charTypeValueId}
                                    value={item?.charTypeValueId}
                                  >
                                    {getValue(item)}
                                  </Select.Option>
                                ))}
                            </Select>
                          </Form.Item>
                        );
                      }
                    }

                    if (selectedCharType?.dataTypeId === 1) {
                      return (
                        <Form.Item
                          label={<div style={{ whiteSpace: "normal" }}>Значение по умолчанию</div>}
                          className="form-item-required__hidden-error__mb0"
                          name="defaultValue"
                          rules={[{ type: "number", message: "Введите число" }]}
                          getValueFromEvent={(e) =>
                            e.target.value ? Number(e.target.value) : undefined
                          }
                          labelCol={{ span: 11 }}
                          wrapperCol={{ span: 13 }}
                        >
                          <Input
                            type="number"
                            placeholder="Введите число"
                            allowClear
                            disabled={!charTypeId}
                          />
                        </Form.Item>
                      );
                    }
                    if (selectedCharType?.dataTypeId === 2) {
                      return (
                        <Form.Item
                          label={<div style={{ whiteSpace: "normal" }}>Значение по умолчанию</div>}
                          className="form-item-required__hidden-error__mb0"
                          name="defaultValue"
                          rules={[{ type: "string", message: "Введите строку" }]}
                          labelCol={{ span: 11 }}
                          wrapperCol={{ span: 13 }}
                        >
                          <Input
                            type="text"
                            placeholder="Введите строку"
                            allowClear
                            disabled={!charTypeId}
                          />
                        </Form.Item>
                      );
                    }
                    if (selectedCharType?.dataTypeId === 3) {
                      return (
                        <Form.Item
                          label={<div style={{ whiteSpace: "normal" }}>Значение по умолчанию</div>}
                          className="form-item-required__hidden-error__mb0"
                          name="defaultValue"
                          labelCol={{ span: 11 }}
                          wrapperCol={{ span: 13 }}
                        >
                          <DatePicker
                            style={{ width: "100%" }}
                            format={displayFormat}
                            allowClear
                            disabled={!charTypeId}
                          />
                        </Form.Item>
                      );
                    }
                    if (selectedCharType?.dataTypeId === 4) {
                      return (
                        <Form.Item
                          label={<div style={{ whiteSpace: "normal" }}>Значение по умолчанию</div>}
                          className="form-item-required__hidden-error__mb0"
                          name="defaultValue"
                          labelCol={{ span: 11 }}
                          wrapperCol={{ span: 13 }}
                        >
                          <Select
                            style={{ width: "100%" }}
                            placeholder="Выберите значение"
                            allowClear
                            disabled={!charTypeId}
                          >
                            {getCamelizeDictionaries(selectedCharType?.valueDicTypeAlias)?.map(
                              (item) => (
                                <Select.Option key={item.id} value={item.id}>
                                  {item.name}
                                </Select.Option>
                              )
                            )}
                          </Select>
                        </Form.Item>
                      );
                    }
                    if (selectedCharType?.dataTypeId === 5) {
                      return (
                        <Form.Item
                          label={<div style={{ whiteSpace: "normal" }}>Значение по умолчанию</div>}
                          className="form-item-required__hidden-error__mb0"
                          name="defaultValue"
                          rules={[{ type: "integer", message: "Введите целое число" }]}
                          getValueFromEvent={(e) =>
                            e.target.value ? Number(e.target.value) : undefined
                          }
                          labelCol={{ span: 11 }}
                          wrapperCol={{ span: 13 }}
                        >
                          <Input
                            type="number"
                            step="1"
                            placeholder="Введите число"
                            allowClear
                            disabled={!charTypeId}
                          />
                        </Form.Item>
                      );
                    }
                    if (selectedCharType?.dataTypeId === 6) {
                      return (
                        <Form.Item
                          label={<div style={{ whiteSpace: "normal" }}>Значение по умолчанию</div>}
                          className="form-item-required__hidden-error__mb0"
                          name="defaultValue"
                          rules={[
                            { type: "integer", min: 1, message: "Введите натуральное число" },
                          ]}
                          getValueFromEvent={(e) =>
                            e.target.value ? Number(e.target.value) : undefined
                          }
                          labelCol={{ span: 11 }}
                          wrapperCol={{ span: 13 }}
                        >
                          <Input
                            type="number"
                            min="1"
                            step="1"
                            placeholder="Введите число"
                            allowClear
                            disabled={!charTypeId}
                          />
                        </Form.Item>
                      );
                    }

                    return (
                      <Form.Item
                        label={<div style={{ whiteSpace: "normal" }}>Значение по умолчанию</div>}
                        className="form-item-required__hidden-error__mb0"
                        name="defaultValue"
                        labelCol={{ span: 11 }}
                        wrapperCol={{ span: 13 }}
                      >
                        <Input maxLength={255} allowClear disabled={!charTypeId} />
                      </Form.Item>
                    );
                  }}
                </Form.Item>
              </Col>
            ) : (
              <>{getRenderDefaultValueViewMode()}</>
            )}
          </Row>
        </Spin>
      </Row>
    );
  };

  const onSetValuesMode = (mode: "view" | "edit_close_date" | "edit") => {
    if (typeCharacteristicObject && typesCharacteristicsObjectParamsForm) {
      if (mode === "edit_close_date") {
        typesCharacteristicsObjectParamsForm.setFieldsValue({
          toDate: moment(typeCharacteristicObject.toDate),
        });
      }

      if (mode === "view") {
        typesCharacteristicsObjectParamsForm.setFieldsValue({
          toDate: moment(typeCharacteristicObject.toDate),
        });
      }

      if (mode === "edit") {
        const getFormatDefaultValue = () => {
          const getFormatForDataTypeId = (typeCharacteristicObject?: TypeCharacteristicObject) => {
            const defVal = typeCharacteristicObject?.defaultValue;

            switch (selectedCharType?.dataTypeId) {
              case 3:
                return defVal ? moment(defVal) : undefined;
              case 4:
                return defVal ? Number(defVal) : undefined;
              default:
                return defVal;
            }
          };

          if (selectedCharType?.charTypeValues && selectedCharType?.charTypeValues?.length > 0) {
            if (typeCharacteristicObject?.defaultValue) {
              const findSelectedValue = typeCharacteristicObject?.objectCharTypeValues?.find(
                (v) => {
                  switch (selectedCharType?.dataTypeId) {
                    case 3:
                      return moment(v?.charTypeValue?.value).isSame(
                        moment(typeCharacteristicObject?.defaultValue),
                        "day"
                      );
                    default:
                      return v?.charTypeValue?.value === typeCharacteristicObject?.defaultValue;
                  }
                }
              );

              if (findSelectedValue) {
                return findSelectedValue.charTypeValueId;
              }
            }
          }

          return getFormatForDataTypeId();
        };

        typesCharacteristicsObjectParamsForm.setFieldsValue({
          charTypeId: typeCharacteristicObject?.charTypeId,

          name: typeCharacteristicObject?.name,
          minCardinality: typeCharacteristicObject?.minCardinality,
          maxCardinality: typeCharacteristicObject?.maxCardinality,
          defaultValue: getFormatDefaultValue(),
          alias: typeCharacteristicObject?.alias,
          accessModeId: typeCharacteristicObject?.accessModeId,
          viewModeMask: typeCharacteristicObject?.viewModeMask,
        });
      }
    }

    setMode && setMode(mode);
  };

  const onClickCancel = () => {
    onSetValuesMode("view");
  };

  const onClickApply = () => {
    if (typesCharacteristicsObjectParamsForm) {
      typesCharacteristicsObjectParamsForm.submit();
    }
  };

  return (
    <div className="types-characteristics-params-container">
      {mode === "create" && <>{renderParamsPanel()}</>}
      {mode !== "create" && (
        <Collapse
          bordered={false}
          activeKey={activeCollapseKey}
          onChange={onChangeCollapse}
          className="types-characteristics-params-container__collapse"
        >
          <Collapse.Panel
            key="1"
            header={
              <Row
                style={{ width: "100%" }}
                justify="space-between"
                className="types-characteristics-create-container__pageTitle"
              >
                <Col>
                  <Row style={{ width: "100%" }}>
                    <h3 style={{ margin: 0 }}>Тип характеристики (объект)</h3>
                  </Row>
                </Col>
                <Col>
                  <Row wrap={false}>
                    {hasAccess(MENU_ADMIN_OBJECT_CHAR_TYPE_WRITE) && (
                      <>
                        {mode === "edit_close_date" || mode === "edit" ? (
                          <>
                            <SuccessIcon
                              className="types-characteristics-params-container__successRecordButton"
                              onClick={(e) => {
                                e.stopPropagation();
                                onClickApply();
                              }}
                            />
                            <CancelIcon
                              className="types-characteristics-params-container__cancelRecordButton"
                              onClick={(e) => {
                                e.stopPropagation();
                                onClickCancel();
                              }}
                            />
                          </>
                        ) : (
                          <>
                            <EditIcon
                              className="types-characteristics-params-container__editRecordButton"
                              onClick={(e) => {
                                e.stopPropagation();
                                onSetValuesMode("edit");
                              }}
                            />
                            <CloseDateIcon
                              className="types-characteristics-params-container__closeRecordButton"
                              onClick={(e) => {
                                e.stopPropagation();
                                onSetValuesMode("edit_close_date");
                              }}
                            />
                          </>
                        )}
                      </>
                    )}
                    <Button
                      onClick={(e) => {
                        e.stopPropagation();
                        showHistoryModal(Number(typeCharacteristicObject?.id));
                      }}
                      className="iconButton types-characteristics-values-table__threeDotsButton"
                      type="text"
                      htmlType="button"
                      icon={<DotsIcon />}
                    />
                  </Row>
                </Col>
              </Row>
            }
          >
            {renderParamsPanel()}
          </Collapse.Panel>
        </Collapse>
      )}

      <HistoryAndPropertyModal
        objectTypeSysname="object_char_type"
        objectId={objectId}
        handleHideModal={() => {
          setHistoryModalVisible(false);
        }}
        visibleModal={historyModalVisible}
        hasAccessToEdit={hasAccess(MENU_ADMIN_OBJECT_CHAR_TYPE_WRITE)}
      />
    </div>
  );
};

export default TypesCharacteristicsObjectParams;
